import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { projectsApi } from '../services/api';

const ProjectsPage = () => {
  const { t } = useTranslation();
  const { isRTL, currentLang } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    document.title = currentLang === 'ar'
      ? 'مشاريعنا | أرمان كيتشنز'
      : 'Our Projects | Arman Kitchens';
  }, [currentLang]);
  const [loading, setLoading] = useState(true);

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  const categories = [
    { id: 'all', name: t('projects.categories.all') },
    { id: 'wood', name: t('projects.categories.wood') },
    { id: 'aluminum', name: t('projects.categories.aluminum') },
    { id: 'modern', name: t('projects.categories.modern') },
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = activeCategory === 'all' 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <main data-testid="projects-page" className="pt-24">
      {/* Hero Banner */}
      <section className="relative h-[40vh] min-h-[300px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg)' }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-text mb-4 block">{t('projects.label')}</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              {t('projects.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-wrap gap-3 mb-12"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                data-testid={`filter-${cat.id}`}
                className={`px-6 py-3 text-sm uppercase tracking-wider transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-gold hover:text-charcoal'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </motion.div>

          {/* Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-[4/5] bg-muted animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`project-${project.id}`}
                  className="project-card group relative aspect-[4/5] overflow-hidden cursor-pointer"
                >
                  <Link to={`/projects/${project.id}`}>
                    <img
                      src={project.images?.[0] || 'https://images.pexels.com/photos/29925933/pexels-photo-29925933.jpeg'}
                      alt={currentLang === 'ar' ? project.title_ar : project.title_en}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                    <div className="absolute inset-0 p-6 flex flex-col justify-end">
                      <span className="text-gold text-xs uppercase tracking-wider mb-2">
                        {t(`projects.categories.${project.category}`)}
                      </span>
                      <h3 className="text-white text-xl font-semibold group-hover:text-gold transition-colors">
                        {currentLang === 'ar' ? project.title_ar : project.title_en}
                      </h3>
                      <p className="text-white/60 text-sm mt-2 line-clamp-2">
                        {currentLang === 'ar' ? project.description_ar : project.description_en}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}

          {filteredProjects.length === 0 && !loading && (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">
                {currentLang === 'ar' ? 'لا توجد مشاريع في هذه الفئة' : 'No projects in this category'}
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default ProjectsPage;
